import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useLazyQuery } from '@apollo/react-hooks'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, ChevronLeft, ChevronRight, Download, Lock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import { formatDateTimeLocal } from '@/utils/timezone'
import { API_URL } from '../../utils'
import { ACCESS_PHOTO_SHARE, SHARED_PHOTOS } from '../photos/api'
import { sharedPhotoDownloadUrl, sharedZipUrl } from '../photos/lib/share'
import { sharedPhotosView } from './sharedPhotosState'
import SharedPhotosPageSkeleton from './SharedPhotosPageSkeleton'
import logo from '../../assets/img/logo_signin.png'

interface SharedPhoto {
  id: string
  thumbUrl: string
  displayUrl: string
  width: number | null
  height: number | null
}

const SharedPhotosPage: React.FC = () => {
  const { token } = useParams<{ token: string }>()
  const { t, i18n } = useTranslation()
  const [password, setPassword] = useState('')
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  const [pwRequired, setPwRequired] = useState(false)
  const [access, { data: accessData, loading: accessLoading, called, error: accessError, refetch: refetchAccess }] = useLazyQuery(ACCESS_PHOTO_SHARE, {
    fetchPolicy: 'network-only',
    onCompleted: (d) => setPwRequired(!!d?.accessPhotoShare?.requiresPassword),
  })
  const [loadPhotos, { data: photosData, loading: photosLoading, called: photosCalled, error: photosError, refetch: refetchPhotos }] = useLazyQuery(SHARED_PHOTOS, {
    fetchPolicy: 'network-only',
  })

  useEffect(() => {
    if (token) access({ variables: { token } })
    // run once per token
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  const result = accessData?.accessPhotoShare
  const opened = !!result?.success

  useEffect(() => {
    if (opened && token) loadPhotos({ variables: { token } })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, token])

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    access({ variables: { token, password } })
  }

  const shared = photosData?.sharedPhotos
  const photos: SharedPhoto[] = shared?.photos || []
  const current = openIndex !== null ? photos[openIndex] : null

  const step = (delta: number) =>
    setOpenIndex((i) => (i === null || !photos.length ? i : (i + delta + photos.length) % photos.length))

  const onViewerKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') step(-1)
    else if (e.key === 'ArrowRight') step(1)
  }

  const view = sharedPhotosView({
    accessCalled: called,
    accessLoading,
    accessFailed: !!accessError,
    access: result,
    pwRequired,
    photosCalled,
    photosLoading,
    photosFailed: !!photosError,
    shared,
  })
  const [retrying, setRetrying] = useState(false)
  const retry = () => {
    setRetrying(true)
    Promise.resolve(accessError ? refetchAccess() : refetchPhotos())
      .catch(() => undefined)
      .then(() => setRetrying(false))
  }

  return (
    <div className="min-h-screen bg-muted py-10">
      <div className="mx-auto w-full max-w-6xl px-4">
        <div className="mb-6 flex justify-center">
          <img src={logo} alt="Urkla" className="h-20" />
        </div>

        {view === 'password' && (
          <Card className="mx-auto max-w-xl">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Lock className="h-5 w-5 text-primary" />
                <CardTitle className="text-lg">{t('Photos_public_password_title')}</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={submit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="share-password">{t('Photos_public_password')}</Label>
                  <Input
                    type="password"
                    id="share-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoFocus
                  />
                </div>
                {!accessLoading && result?.error === 'password_invalid' && (
                  <Alert variant="destructive">
                    <AlertDescription>{t('Photos_public_password_invalid')}</AlertDescription>
                  </Alert>
                )}
                <Button type="submit" disabled={!password || accessLoading}>
                  {accessLoading && <Spinner />}
                  {t('Photos_public_open')}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {view === 'invalid' && (
          <Card className="mx-auto max-w-xl">
            <CardContent className="flex flex-col items-center justify-center gap-3 py-12 text-center">
              <AlertTriangle className="h-12 w-12 text-[hsl(var(--warning))]" />
              <p className="text-muted-foreground">{t('Photos_public_link_invalid')}</p>
            </CardContent>
          </Card>
        )}

        {view === 'error' && (
          <Card className="mx-auto max-w-xl">
            <CardContent className="flex flex-col items-center justify-center gap-3 py-12 text-center">
              <AlertTriangle className="h-12 w-12 text-[hsl(var(--warning))]" />
              <p className="text-muted-foreground">{t('Photos_public_load_error')}</p>
              <Button variant="outline" onClick={retry} disabled={retrying}>
                {retrying && <Spinner />}
                {t('Photos_public_retry')}
              </Button>
            </CardContent>
          </Card>
        )}

        {view === 'loading' && <SharedPhotosPageSkeleton />}

        {view === 'empty' && (
          <p className="py-12 text-center text-muted-foreground">{t('Photos_public_empty')}</p>
        )}

        {view === 'loaded' && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div className="min-w-0">
                <h1 className="truncate text-xl font-semibold">{shared.projectName}</h1>
                <p className="text-sm text-muted-foreground">
                  {t('Photos_public_count', { count: photos.length })}
                  {shared.expiresAt &&
                    ` · ${t('Photos_public_valid_until', { date: formatDateTimeLocal(shared.expiresAt, i18n.language, 'P') })}`}
                </p>
              </div>
              <Button asChild size="sm">
                <a href={sharedZipUrl(API_URL, token)}>
                  <Download className="h-4 w-4" />
                  {t('Photos_public_download_all')}
                </a>
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
              {photos.map((p, i) => (
                <button
                  key={p.id}
                  type="button"
                  aria-label={t('Photos_public_photo_n', { n: i + 1, total: photos.length })}
                  onClick={() => setOpenIndex(i)}
                  className="overflow-hidden rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <img src={p.thumbUrl} alt="" loading="lazy" className="aspect-square w-full rounded-md object-cover" />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <Dialog open={!!current} onOpenChange={(o) => !o && setOpenIndex(null)}>
        <DialogContent className="max-w-5xl" onKeyDown={onViewerKeyDown}>
          <DialogTitle className="sr-only">{shared?.projectName}</DialogTitle>
          {current && (
            <div className="space-y-3">
              <div className="relative">
                <img src={current.displayUrl} alt="" className="max-h-[80vh] w-full object-contain" />
                {photos.length > 1 && (
                  <>
                    <Button
                      variant="secondary"
                      size="icon"
                      className="absolute left-1 top-1/2 -translate-y-1/2"
                      onClick={() => step(-1)}
                      aria-label={t('Photos_public_previous')}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="secondary"
                      size="icon"
                      className="absolute right-1 top-1/2 -translate-y-1/2"
                      onClick={() => step(1)}
                      aria-label={t('Photos_public_next')}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
              <div className="flex justify-end">
                <Button asChild size="sm" variant="outline">
                  <a href={sharedPhotoDownloadUrl(API_URL, token, current.id)}>
                    <Download className="h-4 w-4" />
                    {t('Photos_public_download')}
                  </a>
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default SharedPhotosPage
